import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Users, Download, Trash2, Loader2, FileText, Mail } from "lucide-react";
import { Input } from "../../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TeamsAdminView({ token, onBack }) {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [weekLabel, setWeekLabel] = useState("8/10 – 8/14");
  const [downloading, setDownloading] = useState(null);
  const [pdfEmail, setPdfEmail] = useState("");
  const [emailing, setEmailing] = useState(false);
  const headers = { Authorization: `Bearer ${token || localStorage.getItem("token")}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/teams/admin/all`, { headers });
      setTeams(res.data.teams || []);
      setWeekLabel(res.data.week_label || "8/10 – 8/14");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = (groupCode) => {
    const t = token || localStorage.getItem("token");
    const params = new URLSearchParams({ token: t });
    if (groupCode) params.set("group_code", groupCode);
    window.open(`${API}/teams/admin/pdf?${params.toString()}`, "_blank");
  };

  const emailPdf = async () => {
    if (!pdfEmail || !pdfEmail.includes("@")) { toast.error("Enter a valid email address"); return; }
    setEmailing(true);
    try {
      const res = await axios.post(`${API}/teams/admin/pdf/email`, { email: pdfEmail }, { headers });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    }
    setEmailing(false);
  };

  const resetGroup = async (groupCode) => {
    if (!window.confirm(`Reset teams for ${groupCode}? The head counselor will be able to re-do them.`)) return;
    try {
      await axios.delete(`${API}/teams/admin/${groupCode}`, { headers });
      toast.success(`Teams reset for ${groupCode}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="teams-admin-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" /> Teams
            </h2>
            <p className="text-sm text-muted-foreground">Week of {weekLabel} — {teams.length} group{teams.length === 1 ? "" : "s"} saved</p>
          </div>
        </div>
        {teams.length > 0 && (
          <Button
            onClick={() => downloadPdf(null)}
            disabled={downloading === "all"}
            className="bg-indigo-600 hover:bg-indigo-700 gap-2"
            data-testid="teams-download-all-btn"
          >
            {downloading === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Download All (PDF)
          </Button>
        )}
      </div>

      {teams.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border bg-indigo-50/50 border-indigo-200" data-testid="teams-email-bar">
          <Mail className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-bold text-indigo-900">Email the full Teams PDF (best on phones):</span>
          <Input type="email" value={pdfEmail} onChange={(e) => setPdfEmail(e.target.value)} placeholder="you@example.com" className="h-8 w-56 text-xs bg-white" data-testid="teams-email-input" />
          <Button size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={emailPdf} disabled={emailing} data-testid="teams-email-btn">
            {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Email PDF
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : teams.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="teams-admin-empty">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No teams saved yet. Head counselors save their teams from the Teams card in their login.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {teams.map((t) => (
            <div key={t.group_code} className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden" data-testid={`teams-admin-group-${t.group_code}`}>
              <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-black text-slate-800">{t.group_code} — {t.group_name}</p>
                  <p className="text-[10px] text-muted-foreground">Saved {t.saved_at ? new Date(t.saved_at).toLocaleString() : ""}</p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => downloadPdf(t.group_code)} disabled={downloading === t.group_code} data-testid={`teams-download-${t.group_code}`}>
                    {downloading === t.group_code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => resetGroup(t.group_code)} data-testid={`teams-reset-${t.group_code}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 divide-x">
                {[["Team 1", t.team1, "text-blue-700 bg-blue-50"], ["Team 2", t.team2, "text-red-700 bg-red-50"]].map(([label, list, cls]) => (
                  <div key={label}>
                    <div className={`px-3 py-1.5 text-xs font-black text-center ${cls}`}>{label} ({(list || []).length})</div>
                    <ul className="divide-y">
                      {(list || []).map((k) => (
                        <li key={k.id} className="px-3 py-1.5 text-xs font-medium text-slate-700">{k.name}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
